import { useState } from "react";
import { motion } from "framer-motion";
import { Ruler, Users, BedDouble, UserCog, ArrowRight, ArrowUpRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { vessels } from "../data/vessels";
import { DetailModal } from "./DetailModal";

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Fleet = ({ onEnquire }) => {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(null);

  return (
    <section id="fleet" className="relative py-24 sm:py-36 bg-[#050B14]" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mb-16 sm:mb-20"
        >
          <p className="font-label text-[10px] sm:text-xs tracking-[0.45em] uppercase text-[#D4AF37] mb-5" data-testid="fleet-overline">
            {t.fleet.overline}
          </p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F7F5F0] leading-[1.05]" data-testid="fleet-title">
            {t.fleet.title}
          </h2>
          <p className="mt-6 text-sm sm:text-base text-slate-400 font-light leading-relaxed">{t.fleet.sub}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {vessels.map((v, i) => (
            <motion.article
              key={v.id}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={cardVariants}
              onClick={() => setSelected(v)}
              className={`group relative cursor-pointer ${v.featured ? "md:col-span-2" : ""}`}
              data-testid={`vessel-card-${v.id}`}
            >
              <div className="relative overflow-hidden border border-[rgba(212,175,55,0.15)] group-hover:border-[rgba(212,175,55,0.45)] transition-colors duration-700">
                <div className={`overflow-hidden ${v.featured ? "aspect-[21/9]" : "aspect-[16/10]"}`}>
                  <img
                    src={v.image}
                    alt={v.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/90 via-transparent to-transparent" />
                <span className="absolute top-5 left-5 font-label text-[9px] tracking-[0.35em] uppercase bg-[#050B14]/70 backdrop-blur-md border border-[rgba(212,175,55,0.35)] text-[#D4AF37] px-4 py-2">
                  {t.fleet.categories[v.category]}
                </span>
                <span className="absolute top-5 right-5 flex items-center gap-2 font-label text-[9px] tracking-[0.25em] uppercase bg-[#050B14]/70 backdrop-blur-md border border-[rgba(212,175,55,0.35)] text-[#F3E5AB] px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  {t.details.common.detailsHint}
                  <ArrowUpRight size={12} />
                </span>
              </div>

              <div className="pt-7 flex flex-wrap items-end justify-between gap-6">
                <div className="min-w-0">
                  <h3 className="font-display text-3xl sm:text-4xl text-[#F7F5F0] font-light tracking-wide">{v.name}</h3>
                  <p className="mt-3 text-sm text-slate-400 font-light leading-relaxed max-w-lg">{t.fleet.vessels[v.id]}</p>
                  <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
                    {[
                      { icon: Ruler, label: t.fleet.specs.loa, value: v.specs.loa },
                      { icon: Users, label: t.fleet.specs.guests, value: v.specs.guests },
                      { icon: BedDouble, label: t.fleet.specs.cabins, value: v.specs.cabins },
                      { icon: UserCog, label: t.fleet.specs.crew, value: v.specs.crew },
                    ].map(({ icon: Icon, label, value }) => (
                      <div key={label} className="flex items-center gap-2.5">
                        <Icon size={15} className="text-[#D4AF37]" strokeWidth={1.5} />
                        <span className="font-label text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</span>
                        <span className="text-sm text-[#F7F5F0]">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-4">
                  <p className="font-label text-xs tracking-[0.15em] text-slate-400">
                    {t.fleet.from}{" "}
                    <span className="font-display text-2xl text-[#D4AF37]">{v.price}</span>{" "}
                    {t.fleet.perWeek}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEnquire(v.name);
                    }}
                    data-testid={`vessel-enquire-${v.id}`}
                    className="group/btn flex items-center gap-3 font-label text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] border-b border-[#D4AF37]/40 pb-1.5 hover:text-[#F3E5AB] hover:border-[#F3E5AB] transition-colors duration-300"
                  >
                    {t.fleet.enquire}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1.5" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <DetailModal
        open={!!selected}
        onClose={() => setSelected(null)}
        image={selected?.image}
        images={selected ? [selected.image, ...selected.gallery] : undefined}
        imageAlt={selected?.name}
        tag={selected ? t.fleet.categories[selected.category] : ""}
        title={selected?.name}
        testid="vessel-modal"
      >
        {selected && (
          <>
            <p className="text-sm sm:text-base text-slate-300 font-light leading-relaxed">{t.fleet.vessels[selected.id]}</p>
            <p className="mt-4 text-sm sm:text-base text-slate-400 font-light leading-relaxed">{t.details.vessels[selected.id].descLong}</p>

            <div className="mt-10">
              <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5">{t.details.common.spec}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[rgba(212,175,55,0.18)] border border-[rgba(212,175,55,0.18)]" data-testid="vessel-modal-specs">
                {[
                  [t.fleet.specs.loa, selected.specs.loa],
                  [t.details.common.beam, selected.specs.beam],
                  [t.details.common.year, selected.specs.year],
                  [t.details.common.speed, selected.specs.speed],
                  [t.fleet.specs.guests, selected.specs.guests],
                  [t.fleet.specs.cabins, selected.specs.cabins],
                  [t.fleet.specs.crew, selected.specs.crew],
                  [t.fleet.from, `${selected.price} ${t.fleet.perWeek}`],
                ].map(([label, value], idx) => (
                  <div key={idx} className="bg-[#0B1528] p-4 sm:p-5">
                    <p className="font-label text-[9px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">{label}</p>
                    <p className="font-display text-lg sm:text-xl text-[#F7F5F0]">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5">{t.details.common.options}</p>
                <ul className="space-y-3" data-testid="vessel-modal-options">
                  {t.details.vessels[selected.id].amenities.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-sm text-slate-300 font-light">
                      <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37] mt-1.5 shrink-0" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-label text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-5">{t.details.common.services}</p>
                <ul className="space-y-3" data-testid="vessel-modal-services">
                  {t.details.services.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-slate-300 font-light">
                      <span className="w-1.5 h-1.5 rotate-45 bg-[#D4AF37] mt-1.5 shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-[rgba(212,175,55,0.18)] flex flex-wrap items-center justify-between gap-6">
              <p className="font-label text-xs tracking-[0.15em] text-slate-400">
                {t.fleet.from} <span className="font-display text-3xl text-[#D4AF37]">{selected.price}</span> {t.fleet.perWeek}
              </p>
              <button
                onClick={() => {
                  const name = selected.name;
                  setSelected(null);
                  setTimeout(() => onEnquire(name), 120);
                }}
                data-testid="vessel-modal-enquire-button"
                className="font-label text-[11px] tracking-[0.25em] uppercase px-8 py-4 bg-[#D4AF37] text-[#050B14] font-semibold hover:bg-[#F3E5AB] transition-colors duration-300"
              >
                {t.details.common.enquireCta}
              </button>
            </div>
          </>
        )}
      </DetailModal>
    </section>
  );
};
